
// Deployment trigger: 2026-04-24
import React, { useRef, useEffect, useState } from 'react';
import { Hand, Trophy } from 'lucide-react';
import { Prize } from '../../types';

interface ScratchCardProps {
  gameState: string;
  winningPrize: Prize | null;
  onStart: () => void;
  onProgress: (progress: number) => void;
  isLightMode?: boolean;
}

const WIN_THRESHOLD = 65; // Percentage of surface cleared to trigger a win

const ScratchCard: React.FC<ScratchCardProps> = ({ gameState, winningPrize, onStart, onProgress, isLightMode }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);
  const [brushSize, setBrushSize] = useState(30);
  const [localProgress, setLocalProgress] = useState(0);

  useEffect(() => {
    if (canvasRef.current && (gameState === 'spinning' || gameState === 'idle')) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, isLightMode ? '#e2e8f0' : '#94a3b8');
      grad.addColorStop(0.5, isLightMode ? '#f8fafc' : '#cbd5e1');
      grad.addColorStop(1, isLightMode ? '#e2e8f0' : '#94a3b8');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = isLightMode ? '#475569' : '#1e293b';
      ctx.font = 'bold 36px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('SCRATCH TO REVEAL', canvas.width / 2, canvas.height / 2 + 12);
      setLocalProgress(0);
    }
  }, [gameState, isLightMode]);

  const scratch = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isScratching || gameState !== 'spinning' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    let x, y;
    if ('touches' in e) {
      if (e.cancelable) e.preventDefault();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = (e as React.MouseEvent).clientX - rect.left;
      y = (e as React.MouseEvent).clientY - rect.top;
    }

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x * scaleX, y * scaleY, brushSize, 0, Math.PI * 2);
    ctx.fill();
    
    // Slightly increase brush size as they scratch to make it faster/satisfying
    setBrushSize(prev => Math.min(prev + 0.1, 80));

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 4) { if (pixels[i] === 0) transparentPixels++; }
    const actualProgress = (transparentPixels / (pixels.length / 4)) * 100;
    
    setLocalProgress(actualProgress);
    onProgress(actualProgress);
  };

  // Normalize progress so the bar hits 100% when threshold is reached
  const displayProgress = Math.min(100, Math.round((localProgress / WIN_THRESHOLD) * 100));

  return (
    <div className={`flex flex-col items-center w-full px-6 py-12 rounded-[48px] shadow-2xl border ${isLightMode ? 'bg-white border-slate-200' : 'bg-[#0b0f1a] border-[#1e293b]'}`}>
      <div className="text-center mb-8">
        <h3 className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-black text-xl uppercase tracking-widest italic mb-2`}>Scratch & Win</h3>
        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest">Uncover the surface to claim your gift</p>
      </div>

      <div className={`relative w-full aspect-video max-w-[440px] mb-8 rounded-[32px] border-4 overflow-hidden group shadow-2xl ${isLightMode ? 'bg-indigo-50 border-slate-200' : 'bg-[#1e234a] border-[#1e293b]'}`}>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
            <Trophy className="w-16 h-16 text-amber-400 mb-4 animate-bounce" />
            <div className={`${isLightMode ? 'text-slate-800' : 'text-white'} font-black text-3xl uppercase tracking-tighter leading-tight italic`}>
              {winningPrize?.name || 'Grand Prize'}
            </div>
            <p className="text-indigo-600 font-bold text-sm mt-2">YOU WON!</p>
        </div>

        {(gameState === 'spinning' || gameState === 'finished' || gameState === 'idle') && (
          <canvas
            ref={canvasRef} 
            width={800} 
            height={450}
            className={`absolute inset-0 w-full h-full z-10 transition-opacity duration-1000 cursor-crosshair ${gameState === 'finished' ? 'opacity-0 scale-110 pointer-events-none' : 'opacity-100'}`}
            onMouseDown={() => setIsScratching(true)} 
            onMouseUp={() => setIsScratching(false)} 
            onMouseMove={scratch}
            onTouchStart={(e) => { setIsScratching(true); scratch(e); }} 
            onTouchEnd={() => setIsScratching(false)} 
            onTouchMove={scratch}
          />
        )}
        
        {gameState === 'idle' && (
          <div onClick={onStart} className={`absolute inset-0 z-20 backdrop-blur-sm flex flex-col items-center justify-center font-black uppercase cursor-pointer group ${isLightMode ? 'bg-slate-100/90 text-slate-800' : 'bg-slate-800/90 text-white'}`}>
            <Hand className="w-12 h-12 mb-4 group-hover:scale-110 transition-transform" />
            <span>Click to Reveal</span>
          </div>
        )}
      </div>

      <div className="w-full max-w-[440px] px-2">
        <div className="flex justify-between items-end mb-2">
           <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Reveal Progress</span>
           <span className={`text-[10px] font-black ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'}`}>{displayProgress}%</span>
        </div>
        <div className={`h-2 w-full rounded-full overflow-hidden border ${isLightMode ? 'bg-slate-100 border-slate-200' : 'bg-slate-800 border-white/5'}`}>
           <div 
             className="h-full bg-indigo-500 transition-all duration-300"
             style={{ width: `${displayProgress}%` }}
           />
        </div>
      </div>
    </div>
  );
};

export default ScratchCard;
